import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useParams, Link } from 'react-router-dom';
import { useAuth } from '../../api/hooks/useAuth';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import 'react-phone-number-input/style.css';
import userService from '../../api/services/user.service';
import { useToast } from '../../admin/context/ToastContext';
import axiosInstance from '../../api/config/axios.config';
import ENDPOINTS from '../../api/config/endpoints';
import tokenManager from '../../api/utils/tokenManager';
import errorHandler from '../../api/utils/errorHandler';
import MyOrders from '../../components/MyOrders/MyOrders';
import OrderDetailsPage from '../../components/OrderDetailsPage/OrderDetailsPage';
import subscriptionService from '../../api/services/subscription.service';
import orderService from '../../api/services/order.service';
import StateSelector from '../../components/StateSelector/StateSelector';
import './Account.css';

const DEFAULT_COUNTRY_CODE = '+1';
const DEFAULT_PHONE_COUNTRY = 'US';
const MAX_PHONE_DIGITS = 15;

const ID_PROOF_OPTIONS = [
  { value: 'driving_license', label: 'Driving License' },
  { value: 'passport', label: 'Passport' },
  { value: 'state_issued_id', label: 'State Issued ID' },
];

// Each account section gets its own URL - /addresses, not /profile?section=addresses.
// Exported so the router registers one route per section from this single list
// and the two can never drift apart.
export const ACCOUNT_SECTIONS = [
  { id: 'profile', path: '/profile', label: 'My Profile', icon: '👤' },
  { id: 'addresses', path: '/addresses', label: 'My Addresses', icon: '📍' },
  { id: 'orders', path: '/orders', label: 'My Orders', icon: '📦' },
  { id: 'subscriptions', path: '/subscriptions', label: 'Subscriptions', icon: '🔄' },
  { id: 'settings', path: '/settings', label: 'Settings', icon: '⚙️' },
];

const SECTION_ID_BY_PATH = Object.fromEntries(
  ACCOUNT_SECTIONS.map((section) => [section.path, section.id])
);

// A single order lives under its section, e.g. /orders/<uuid>, so it survives
// a refresh and can be linked to directly
export const ORDER_DETAIL_PATH = '/orders/:orderId';

// Order cards key off uuid and fall back to id, so the URL has to agree
const getOrderId = (order) => String(order?.uuid || order?.id || '');

const getSectionIdFromPath = (pathname) => {
  const normalized = pathname.replace(/\/+$/, '').toLowerCase();
  if (SECTION_ID_BY_PATH[normalized]) return SECTION_ID_BY_PATH[normalized];
  const parent = ACCOUNT_SECTIONS.find((section) =>
    normalized.startsWith(`${section.path}/`)
  );
  return parent ? parent.id : 'profile';
};

const REGION_NAME_FORMATTER =
  typeof Intl !== 'undefined' && Intl.DisplayNames
    ? new Intl.DisplayNames(['en'], { type: 'region' })
    : null;

const getCountryNameFromIso = (countryCode) => {
  if (!countryCode) return '';
  return REGION_NAME_FORMATTER?.of(countryCode) || countryCode;
};

const normalizePhoneInput = (value) => {
  const raw = String(value || '');
  const digits = raw.replace(/\D/g, '').slice(0, MAX_PHONE_DIGITS);
  return raw.trim().startsWith('+') ? `+${digits}` : digits;
};


const normalizePhoneValue = (mobile, countryCode = DEFAULT_COUNTRY_CODE) => {
  const rawMobile = String(mobile || '').trim();
  if (!rawMobile) return '';
  if (rawMobile.startsWith('+')) return rawMobile.replace(/[^\d+]/g, '');
  const digits = rawMobile.replace(/\D/g, '');
  if (!digits) return '';
  // Backend stores mobile without the leading '+' (e.g. 917675090123 = +91 7675090123),
  // so first check whether the digits already embed a valid country calling code.
  // Only fall back to prefixing the default country code when they don't —
  // otherwise an Indian number would wrongly become +1 917675090123 (US).
  const withEmbeddedCode = parsePhoneNumberFromString(`+${digits}`);
  if (withEmbeddedCode && withEmbeddedCode.isValid()) {
    return `+${digits}`;
  }
  return `${countryCode}${digits}`;
};

const getUploadedFileUrl = (filePath) => {
  if (!filePath) return '';
  const value = String(filePath);
  if (/^https?:\/\//i.test(value)) {
    return value
      .replace(/^https?:\/\/[^/]+\/uploads\/(https?:\/\/.+)$/i, '$1')
      .replace(/\/uploads\/uploads\/+/i, '/uploads/');
  }
  const base = (process.env.REACT_APP_API_BASE_URL || '').replace('/api/v1', '');
  if (!base) return value;
  const clean = value.replace(/^\/+/, '').replace(/^uploads\/+/i, '');
  return `${base}/uploads/${clean}`;
};

const getDisplayFileName = (value) => {
  if (!value) return '';
  try {
    const url = new URL(value);
    return url.pathname.split('/').filter(Boolean).pop() || value;
  } catch {
    return String(value).split('/').filter(Boolean).pop() || String(value);
  }
};

const AddressFormModal = ({ show, editing, formData, onCancel, onSubmit, onInputChange }) => {
  if (!show) return null;

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-content address-form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{editing ? 'Edit Address' : 'Add New Address'}</h2>
          <button className="modal-close" onClick={onCancel}>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <form onSubmit={onSubmit} className="address-form">
          <div className="form-grid">
            <div className="form-group">
              <label>Full Name <span style={{color: 'red'}}>*</span></label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={onInputChange}
                placeholder="John Doe"
                required
              />
            </div>

            <div className="form-group">
              <label>Email Address <span style={{color: 'red'}}>*</span></label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={onInputChange}
                placeholder="your@email.com"
                required
              />
            </div>
            
            <div className="form-group">
              <label>Phone <span style={{color: 'red'}}>*</span></label>
              <input
                type="tel"
                name="mobile"
                value={formData.mobile}
                onChange={onInputChange}
                placeholder="+1 (555) 123-4567"
                required
              />
            </div>

            <div className="form-group">
              <label>Door/Apt Number <span style={{color: 'red'}}>*</span></label>
              <input
                type="text"
                name="apartment_number"
                value={formData.apartment_number}
                onChange={onInputChange}
                placeholder="Apt 4B"
                required
              />
            </div>
            
            <div className="form-group full-width">
              <label>Street Address <span style={{color: 'red'}}>*</span></label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={onInputChange}
                placeholder="123 Main Street"
                required
              />
            </div>
            
            <div className="form-group">
              <label>City <span style={{color: 'red'}}>*</span></label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={onInputChange}
                placeholder="New York"
                required
              />
            </div>
            
            <div className="form-group">
              <label>State <span style={{color: 'red'}}>*</span></label>
              <StateSelector
                name="state"
                value={formData.state}
                onChange={onInputChange}
                required
              />
            </div>
            
            <div className="form-group full-width">
              <label>Zipcode <span style={{color: 'red'}}>*</span></label>
              <input
                type="text"
                name="pincode"
                value={formData.pincode}
                onChange={onInputChange}
                placeholder="12345"
                required
              />
            </div>
          </div>
          
          <div className="form-actions">
            <button type="button" className="cancel-btn" onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className="save-btn">
              {editing ? 'Update Address' : 'Add Address'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default function Account() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user: authUser, isAuthenticated, loading: authLoading } = useAuth();
  const { showToast } = useToast();

  // Derived from the URL rather than held in state, so the path is the single
  // source of truth - back/forward, refresh and shared links all land correctly
  const activeSection = getSectionIdFromPath(location.pathname);
  const { orderId } = useParams();

  // Signing out sets isAuthenticated to false while this page is still
  // mounted, which would otherwise bounce the user to /login instead of home.
  const isSigningOut = useRef(false);

  // Send an unauthenticated visitor to login - but not someone who just signed out
  useEffect(() => {
    if (isSigningOut.current) return;
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Rewrite legacy ?section= links (bookmarks, old emails, payment receipts)
  // onto the real path so a single canonical URL is left in the address bar
  useEffect(() => {
    const section = new URLSearchParams(location.search).get('section');
    if (!section) return;
    const match = ACCOUNT_SECTIONS.find((item) => item.id === section);
    navigate(match ? match.path : '/profile', { replace: true });
  }, [location.search, navigate]);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    country: '',
    state: '',
    countryCode: DEFAULT_COUNTRY_CODE,
    phone: '',
    phoneValue: '',
    dateOfBirth: '',
    profilePhoto: null
  });
  const [profileImageUrl, setProfileImageUrl] = useState('');
  const [profileImageFilename, setProfileImageFilename] = useState('');
  const [documents, setDocuments] = useState([]);
  const [uploadingDocId, setUploadingDocId] = useState(null);
  const fileInputRef = useRef(null);
  const proofInputRefs = useRef({});
  const [editMode, setEditMode] = useState(false);

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);


  const [addresses, setAddresses] = useState([]);
  const [loadingAddresses, setLoadingAddresses] = useState(false);

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetailLoading, setOrderDetailLoading] = useState(false);
  const [orderDetailError, setOrderDetailError] = useState('');

  const sidebarItems = ACCOUNT_SECTIONS;

  // Subscriptions state (real data)
  const [subsLoading, setSubsLoading] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);
  
  // Pagination state for addresses
  const [addressesPageDisplay, setAddressesPageDisplay] = useState(1);
  const [addressesLimit, setAddressesLimit] = useState(10);
  const [addressesTotal, setAddressesTotal] = useState(0);
  const [addressesHasMore, setAddressesHasMore] = useState(false);
  
  // Pagination state for subscriptions
  const [subsPageDisplay, setSubsPageDisplay] = useState(1);
  const [subsLimit, setSubsLimit] = useState(10);
  const [subsTotal, setSubsTotal] = useState(0);
  const [subsHasMore, setSubsHasMore] = useState(false);

  const handleSignOut = async () => {
    isSigningOut.current = true;
    try {
      await logout();
      showToast('Signed out successfully', 'success');
      navigate('/', { replace: true });
    } catch (error) {
      isSigningOut.current = false;
      showToast('Error signing out', 'error');
    }
  };

  const loadProfile = async () => {
    try {
      const storedUser = tokenManager.getUserData();
      const userUuid =
        (authUser && (authUser.uuid || authUser.id)) ||
        (storedUser && (storedUser.uuid || storedUser.id));
      if (!userUuid) return;
      const endpoint = ENDPOINTS.USER.PROFILE(userUuid);
      const res = await axiosInstance.get(endpoint);
      const data = res.data?.data || res.data || {};
      const incomingCountryCode =
        data.country_code ||
        data.mobile_country_code ||
        data.phone_country_code ||
        DEFAULT_COUNTRY_CODE;
      const phoneValue = normalizePhoneValue(data.mobile || data.phone || '', incomingCountryCode);
      const parsedPhone = parsePhoneNumberFromString(phoneValue);
      const selectedCountry =
        data.country ||
        getCountryNameFromIso(parsedPhone?.country) ||
        '';
      
      // Load documents
      let loadedDocs = [];
      if (data.id_proofs && Array.isArray(data.id_proofs) && data.id_proofs.length > 0) {
        loadedDocs = data.id_proofs.map((proof, idx) => ({
          id: idx + 1,
          type: proof.type || proof.id_proof_type || '',
          number: proof.number || proof.id_proof_number || '',
          fileKey: proof.file || proof.document || proof.id_proof || '',
          fileUrl: proof.file_url || proof.document_url || getUploadedFileUrl(proof.file || proof.document || proof.id_proof || ''),
          fileName: getDisplayFileName(proof.file || proof.document || proof.id_proof || ''),
          file: null
        }));
      } else {
        // Read the flat per-type fields that the update endpoint writes back.
        const flatDocFields = [
          { type: 'driving_license', idField: 'driving_licence_id', fileField: 'driving_licence_file' },
          { type: 'passport', idField: 'passport_id', fileField: 'passport_file' },
          { type: 'state_issued_id', idField: 'state_issued_id', fileField: 'state_issued_file' },
        ];

        flatDocFields.forEach((mapping) => {
          const number = data[mapping.idField] || '';
          const fileKey = data[mapping.fileField] || '';
          if (number || fileKey) {
            loadedDocs.push({
              id: loadedDocs.length + 1,
              type: mapping.type,
              number,
              fileKey,
              fileUrl: getUploadedFileUrl(fileKey),
              fileName: getDisplayFileName(fileKey),
              file: null,
            });
          }
        });

        // Fallback to single document fields if no flat fields were present.
        if (loadedDocs.length === 0) {
          const singleType = data.id_proof_type || data.identity_proof_type || data.proof_type || '';
          const singleNumber = data.id_proof_number || data.proof_number || data.document_number || '';
          const singleFileKey = data.id_proof || data.identity_proof || data.proof_document || '';
          const singleFileUrl = data.id_proof_url || data.identity_proof_url || data.proof_url || getUploadedFileUrl(singleFileKey);

          if (singleType || singleFileKey) {
            loadedDocs = [{
              id: 1,
              type: singleType,
              number: singleNumber,
              fileKey: singleFileKey,
              fileUrl: singleFileUrl,
              fileName: getDisplayFileName(singleFileKey),
              file: null
            }];
          }
        }
      }
      
      // Ensure we have at least one empty document
      if (loadedDocs.length === 0) {
        loadedDocs = [
          { id: 1, type: '', number: '', file: null, fileUrl: '', fileName: '', fileKey: '' }
        ];
      }
      
      setFormData({
        firstName: data.first_name || '',
        lastName: data.last_name || '',
        email: data.email || '',
        country: selectedCountry,
        state: data.state || '',
        countryCode: parsedPhone ? `+${parsedPhone.countryCallingCode}` : incomingCountryCode,
        phone: parsedPhone?.nationalNumber || String(data.mobile || data.phone || '').replace(/\D/g, ''),
        phoneValue,
        dateOfBirth: data.date_of_birth || '',
        profilePhoto: null
      });
      const imagePath = data.image_url || data.profile_pic || data.profile_image || data.avatar_url || '';
      if (imagePath) {
        setProfileImageUrl(getUploadedFileUrl(imagePath));
      } else {
        setProfileImageUrl('');
      }
      setDocuments(loadedDocs);
    } catch (err) {
      console.error('[Account] loadProfile error:', err);
    }
  };

  useEffect(() => {
    loadProfile();
  }, [authUser]);

  useEffect(() => {
    if (activeSection === 'profile') loadProfile();
    if (activeSection === 'addresses') loadAddresses();
    if (activeSection === 'subscriptions') {
      const loadSubs = async () => {
        setSubsLoading(true);
        try {
          const apiPage = Math.max(0, subsPageDisplay - 1);
          const queryLimit = String(subsLimit).toLowerCase() === 'all' ? 100000 : Number(subsLimit);
          
          // Add pagination parameters if the API supports it
          const result = await subscriptionService.userList({ 
            page: apiPage, 
            limit: queryLimit 
          });
          
          if (result.status) {
            const items = Array.isArray(result.data) ? result.data : [];
            setSubscriptions(items);
            
            // Robust total count detection
            const metaTotal = result.count ?? result.total ?? result.meta?.total ?? null;
            
            // If we found a total from the backend, use it.
            // If not, we keep total as null to signal that we are in "stream/hasMore" mode
            if (metaTotal !== null) {
              setSubsTotal(Number(metaTotal));
            } else if (subsPageDisplay === 1 && items.length < queryLimit) {
              // If we're on page 1 and got fewer items than requested, we know the actual total
              setSubsTotal(items.length);
            } else if (subsPageDisplay === 1 && items.length === queryLimit) {
              // If we got exactly the limit on page 1 but no total count, reset total to null
              // to ensure the hasMore logic takes over
              setSubsTotal(null);
            }
            
            setSubsHasMore(items.length === queryLimit);
          } else {
            setSubscriptions([]);
          }
        } catch (err) {
          console.error('[Account] loadSubscriptions error:', err);
          setSubscriptions([]);
        } finally {
          setSubsLoading(false);
        }
      };
      loadSubs();
    }
  }, [activeSection, addressesPageDisplay, addressesLimit, subsPageDisplay, subsLimit]);

  // The open order is driven by the URL. Clicking a card hands the full object
  // over so there is no refetch, but a direct hit on /orders/<uuid> - refresh,
  // bookmark, shared link - arrives with nothing in state and must load it.
  useEffect(() => {
    if (activeSection !== 'orders' || !orderId) {
      setSelectedOrder(null);
      setOrderDetailError('');
      return;
    }
    if (selectedOrder && getOrderId(selectedOrder) === orderId) return;

    let cancelled = false;
    setOrderDetailLoading(true);
    setOrderDetailError('');
    orderService
      .getOrderById(orderId)
      .then((result) => {
        if (cancelled) return;
        // The service hands back the API envelope for some endpoints and the
        // record itself for others, so unwrap and then confirm it really is an
        // order - a { status: false, data: null } body is an object too
        const payload = result?.data ?? result;
        const isOrder =
          payload && typeof payload === 'object' && !Array.isArray(payload) && !!getOrderId(payload);
        if (isOrder) {
          setSelectedOrder(payload);
        } else {
          setOrderDetailError('That order could not be found.');
        }
      })
      .catch(() => {
        if (!cancelled) setOrderDetailError('That order could not be found.');
      })
      .finally(() => {
        if (!cancelled) setOrderDetailLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // selectedOrder is deliberately omitted - including it would re-run this
    // effect the moment the fetch stores the order
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection, orderId]);

  const loadAddresses = async () => {
    setLoadingAddresses(true);
    try {
      const apiPage = Math.max(0, addressesPageDisplay - 1);
      const queryLimit = String(addressesLimit).toLowerCase() === 'all' ? 100000 : Number(addressesLimit);
      
      // Add pagination parameters if the API supports it
      const params = { page: apiPage, limit: queryLimit };
      const result = await userService.listAddresses(params);
      
      if (result.success) {
        const payload = result.data || [];
        const items = Array.isArray(payload) ? payload : (payload.data || []);
        setAddresses(items);
        
        // Robust total count detection
        const metaTotal = result.count ?? result.total ?? payload.total ?? payload.count ?? result.meta?.total ?? result.data?.total ?? result.data?.count ?? null;
        
        // If we found a total from the backend, use it.
        // If not, we keep total as null to signal that we are in "stream/hasMore" mode
        if (metaTotal !== null) {
          setAddressesTotal(Number(metaTotal));
        } else if (addressesPageDisplay === 1 && items.length < queryLimit) {
          // If we're on page 1 and got fewer items than requested, we know the actual total
          setAddressesTotal(items.length);
        } else if (addressesPageDisplay === 1 && items.length === queryLimit) {
          // If we got exactly the limit on page 1 but no total count, reset total to null
          // to ensure the hasMore logic takes over
          setAddressesTotal(null);
        }
        
        setAddressesHasMore(items.length === queryLimit);
      }
    } catch (err) {
      console.error('[Account] loadAddresses error:', err);
      if (!errorHandler.isAuthError(err)) {
        showToast('Failed to load addresses', 'error');
      }
    } finally {
      setLoadingAddresses(false);
    }
  };

  const addDocument = () => {
    setDocuments(prev => {
      if (prev.length >= 3) return prev;
      const newId = prev.length > 0 ? Math.max(...prev.map(d => d.id)) + 1 : 1;
      return [
        ...prev,
        { id: newId, type: '', number: '', file: null, fileUrl: '', fileName: '', fileKey: '' }
      ];
    });
  };

  const removeDocument = (id) => {
    setDocuments(prev => (prev.length > 1 ? prev.filter(doc => doc.id !== id) : prev));
  };

  const updateDocumentField = (id, field, value) => {
    setDocuments(prev => prev.map(doc =>
      doc.id === id ? { ...doc, [field]: value } : doc
    ));
  };

  const handleDocumentFileSelect = async (id, e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    setUploadingDocId(id);

    try {
      const formDataFile = new FormData();
      formDataFile.append('document', file);

      const res = await axiosInstance.post(ENDPOINTS.AUTH.UPLOAD_DOCUMENT, formDataFile, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const body = res.data;
      const fileKey = body?.data?.filename || body?.filename || body?.data?.file_name || body?.file_name;
      const fileUrl = body?.data?.file_url || body?.file_url || body?.data?.document_url || body?.document_url || getUploadedFileUrl(fileKey);
      const fileName = fileKey ? getDisplayFileName(fileKey) : file.name;
      const resolvedUrl = fileUrl || getUploadedFileUrl(fileKey);

      setDocuments(prev => prev.map(doc =>
        doc.id === id
          ? { ...doc, fileKey, fileUrl: resolvedUrl, fileName, file }
          : doc
      ));

      showToast('File uploaded successfully', 'success');
    } catch (err) {
      console.error('[Account] document upload error:', err);
      showToast(err?.response?.data?.message || err.message || 'Failed to upload file', 'error');
    } finally {
      setUploadingDocId(null);
    }
  };

  const handleProfileSave = async () => {
    try {
      const storedUser = tokenManager.getUserData();
      const userUuid =
        (authUser && (authUser.uuid || authUser.id)) ||
        (storedUser && (storedUser.uuid || storedUser.id));
      const first_name = String(formData.firstName || '').trim();
      const last_name = String(formData.lastName || '').trim();
      if (!first_name) {
        showToast('First name is required', 'error');
        return;
      }
      if (!last_name) {
        showToast('Last name is required', 'error');
        return;
      }
      const phoneValue = formData.phoneValue || normalizePhoneValue(formData.phone, formData.countryCode);
      const parsedPhone = parsePhoneNumberFromString(phoneValue || '');
      if (!phoneValue || !parsedPhone) {
        showToast('Mobile number is required', 'error');
        return;
      }
      if (!isValidPhoneNumber(phoneValue)) {
        showToast('Please enter a valid mobile number', 'error');
        return;
      }
      const payload = {
        first_name,
        last_name,
        country: String(formData.country || '').trim(),
        state: String(formData.state || '').trim(),
        mobile: phoneValue
      };
      if (profileImageFilename) {
        payload.profile_pic = profileImageFilename;
      }
      
      const validDocs = documents.filter(doc => doc.type || doc.number || doc.fileKey);
      const incompleteDoc = validDocs.find(
        doc => !doc.type || !doc.number || !(doc.fileKey || doc.fileUrl)
      );
      if (incompleteDoc) {
        showToast('Complete each added document or remove the incomplete one', 'error');
        return;
      }

      const documentFieldMap = {
        driving_license: {
          id: 'driving_licence_id',
          file: 'driving_licence_file',
        },
        passport: {
          id: 'passport_id',
          file: 'passport_file',
        },
        state_issued_id: {
          id: 'state_issued_id',
          file: 'state_issued_file',
        },
      };

      // Always send all document fields; unselected types are sent as empty strings.
      payload.driving_licence_id = '';
      payload.driving_licence_file = '';
      payload.passport_id = '';
      payload.passport_file = '';
      payload.state_issued_id = '';
      payload.state_issued_file = '';

      validDocs.forEach(doc => {
        const mapping = documentFieldMap[doc.type];
        if (mapping) {
          payload[mapping.id] = doc.number;
          payload[mapping.file] = doc.fileKey || '';
        }
      });
      
      const res = await axiosInstance.put(ENDPOINTS.USER.UPDATE_PROFILE(userUuid), payload);
      const updated = res.data?.data || res.data;
      if (updated) {
        tokenManager.setUserData(updated);
        window.dispatchEvent(new Event('userDataUpdated'));
      }
      showToast('Profile saved successfully!', 'success');
      setEditMode(false);
      await loadProfile();
    } catch (err) {
      console.error('[Account] save error:', err);
      showToast(err?.response?.data?.message || err.message || 'Failed to save profile', 'error');
    }
  };

  const handlePasswordUpdate = async () => {
    const { currentPassword, newPassword, confirmPassword } = passwordData;

    // Validation
    if (!currentPassword) {
      showToast('Please enter your current password', 'error');
      return;
    }
    if (!newPassword) {
      showToast('Please enter a new password', 'error');
      return;
    }
    if (newPassword !== confirmPassword) {
      showToast('New passwords do not match', 'error');
      return;
    }
    if (newPassword.length < 6) {
      showToast('New password must be at least 6 characters long', 'error');
      return;
    }

    try {
      const storedUser = tokenManager.getUserData();
      const userUuid =
        (authUser && (authUser.uuid || authUser.id)) ||
        (storedUser && (storedUser.uuid || storedUser.id));

      if (!userUuid) {
        showToast('User session not found. Please log in again.', 'error');
        return;
      }

      const payload = {
        old_password: currentPassword,
        new_password: newPassword
      };

      const res = await axiosInstance.put(ENDPOINTS.AUTH.CHANGE_PASSWORD(userUuid), payload);

      if (res.data?.success || res.status === 200) {
        showToast('Password updated successfully!', 'success');
        // Clear password fields
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        showToast(res.data?.message || 'Failed to update password', 'error');
      }
    } catch (err) {
      console.error('[Account] change password error:', err);
      // Professional error message based on response
      const errorMessage = err.response?.data?.message || err.message || 'An error occurred while changing your password. Please try again.';
      showToast(errorMessage, 'error');
    }
  };

  const handlePhotoChange = () => {
    if (!editMode) {
      showToast('Click Edit Profile to change photo', 'info');
      return;
    }
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const formDataFile = new FormData();
    formDataFile.append('file', file);
    try {
      const res = await axiosInstance.post(ENDPOINTS.AUTH.UPLOAD_FILE, formDataFile, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const body = res.data;
      const filename = body?.data?.filename;
      const imageUrl = body?.data?.image_url || body?.image_url;
      if (filename) {
        setProfileImageFilename(filename);
        if (imageUrl) setProfileImageUrl(imageUrl);
        showToast('Photo uploaded', 'success');
      } else {
        showToast(body?.message || 'Upload succeeded but filename missing', 'warning');
      }
    } catch (err) {
      console.error('[Account] upload error:', err);
      showToast(err?.response?.data?.message || err.message || 'Failed to upload photo', 'error');
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };



  const [showAddressForm, setShowAddressForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [addressFormData, setAddressFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    apartment_number: '',
    address: '',
    city: '',
    state: '',
    pincode: ''
  });

  const handleAddAddress = () => {
    setShowAddressForm(true);
    setEditingAddress(null);
    const storedUser = tokenManager.getUserData();
    // Pre-fill email and mobile from authUser or storedUser if available
    setAddressFormData({
      name: '',
      email: authUser?.email || storedUser?.email || '',
      mobile: authUser?.mobile || authUser?.phone || storedUser?.mobile || storedUser?.phone || '',
      apartment_number: '',
      address: '',
      city: '',
      state: '',
      pincode: ''
    });
  };

  const handleEditAddress = (id) => {
    // Look for address by uuid or id
    const address = addresses.find(addr => (addr.uuid === id || addr.id === id));
    if (address) {
      setEditingAddress(address);
      const storedUser = tokenManager.getUserData();
      setAddressFormData({
        name: address.name || '',
        email: address.email || authUser?.email || storedUser?.email || '',
        mobile: address.mobile || address.phone || authUser?.mobile || authUser?.phone || storedUser?.mobile || storedUser?.phone || '',
        apartment_number: address.apartment_number || '',
        address: address.address || address.street || '',
        city: address.city || '',
        state: address.state || '',
        pincode: address.pincode || address.zip || ''
      });
      setShowAddressForm(true);
    }
  };

  const handleAddressFormSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const storedUser = tokenManager.getUserData();
      const userUuid = 
        (authUser && (authUser.uuid || authUser.id)) || 
        (storedUser && (storedUser.uuid || storedUser.id));

      if (!userUuid) {
        showToast('User session expired. Please log in again.', 'error');
        return;
      }

      // Sanitize mobile number - keep only digits
      const sanitizedMobile = String(addressFormData.mobile || '').replace(/\D/g, '');

      // Construct payload with exactly the fields required by backend
      const payload = {
        user_uuid: userUuid,
        name: addressFormData.name,
        email: addressFormData.email,
        mobile: sanitizedMobile,
        apartment_number: addressFormData.apartment_number,
        address: addressFormData.address,
        city: addressFormData.city,
        state: addressFormData.state,
        pincode: String(addressFormData.pincode || '')
      };


      if (editingAddress) {
        // Update existing address via API
        const addressUuid = editingAddress.uuid || editingAddress.id;
        const result = await userService.updateAddress(addressUuid, payload);
        if (result.success) {
          showToast('Address updated successfully!', 'success');
          await loadAddresses(); // Refresh the list
        }
      } else {
        // Add new address via API
        const result = await userService.createAddress(payload);
        if (result.success) {
          showToast('Address added successfully!', 'success');
          await loadAddresses(); // Refresh the list
        }
      }
      
      setShowAddressForm(false);
      setEditingAddress(null);
      setAddressFormData({
        name: '',
        email: '',
        mobile: '',
        apartment_number: '',
        address: '',
        city: '',
        state: '',
        pincode: ''
      });
    } catch (err) {
      console.error('[Account] Address save error:', err);
      showToast(err.response?.data?.message || err.message || 'Failed to save address', 'error');
    }
  };

  const handleAddressFormCancel = () => {
    setShowAddressForm(false);
    setEditingAddress(null);
    setAddressFormData({
      name: '',
      email: '',
      mobile: '',
      apartment_number: '',
      address: '',
      city: '',
      state: '',
      pincode: ''
    });
  };

  const handleAddressInputChange = (e) => {
    const { name, value } = e.target;
    setAddressFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDeleteAddress = async (id) => {
    if (!window.confirm('Are you sure you want to delete this address?')) return;
    
    try {
      const result = await userService.deleteAddress(id);
      if (result.success) {
        showToast('Address deleted successfully', 'success');
        await loadAddresses(); // Refresh the list
      }
    } catch (err) {
      console.error('[Account] Delete address error:', err);
      showToast(err.message || 'Failed to delete address', 'error');
    }
  };

  const handleOrderClick = (order) => {
    // Keep the object so the detail view paints immediately, and move the URL
    // so the view is refresh-safe and shareable
    setSelectedOrder(order);
    navigate(`/orders/${getOrderId(order)}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCloseOrderDetails = () => {
    navigate('/orders');
  };

  // Pagination handlers for addresses
  const handleAddressesLimitChange = (e) => {
    setAddressesLimit(e.target.value);
    setAddressesPageDisplay(1);
  };

  const handleAddressesPrevPage = () => {
    if (addressesPageDisplay > 1) {
      setAddressesPageDisplay((prev) => prev - 1);
    }
  };

  const handleAddressesNextPage = () => {
    const totalPages = addressesTotal ? Math.ceil(addressesTotal / (String(addressesLimit).toLowerCase() === 'all' ? 100000 : Number(addressesLimit))) : addressesPageDisplay + 1;
    const canNext = addressesTotal ? addressesPageDisplay < totalPages : addressesHasMore;
    if (canNext) {
      setAddressesPageDisplay((prev) => prev + 1);
    }
  };

  // Pagination handlers for subscriptions
  const handleSubsLimitChange = (e) => {
    setSubsLimit(e.target.value);
    setSubsPageDisplay(1);
  };

  const handleSubsPrevPage = () => {
    if (subsPageDisplay > 1) {
      setSubsPageDisplay((prev) => prev - 1);
    }
  };

  const handleSubsNextPage = () => {
    const totalPages = subsTotal ? Math.ceil(subsTotal / (String(subsLimit).toLowerCase() === 'all' ? 100000 : Number(subsLimit))) : subsPageDisplay + 1;
    const canNext = subsTotal ? subsPageDisplay < totalPages : subsHasMore;
    if (canNext) {
      setSubsPageDisplay((prev) => prev + 1);
    }
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'profile':
        return (
          <div className="account-card">
            <div className="section-header">
              <div className="header-left">
                <h2>My Profile</h2>
              </div>
              <div className="header-right">
                {!editMode ? (
                  <button className="add-btn" onClick={() => setEditMode(true)}>Edit Profile</button>
                ) : (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button 
                      className="cancel-btn" 
                      onClick={async () => { setEditMode(false); await loadProfile(); }}
                    >
                      Cancel
                    </button>
                    <button className="save-btn" onClick={handleProfileSave}>
                      Save Changes
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="profile-form">
            <div className="profile-photo-section">
              <div className="photo-upload">
                <div className="photo-placeholder" style={{ overflow: 'hidden' }}>
                  {profileImageUrl ? (
                    <img src={profileImageUrl} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <span className="photo-icon">👤</span>
                  )}
                </div>
                <button className="upload-btn" onClick={handlePhotoChange} disabled={!editMode}>Change Photo</button>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleFileSelected}
                />
              </div>
            </div>
              
              <div className="form-grid">
                <div className="form-group">
                  <label>First Name</label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    disabled={!editMode}
                  />
                </div>
                <div className="form-group">
                  <label>Last Name</label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    required
                    disabled={!editMode}
                  />
                </div>
                
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={() => {}}
                    readOnly
                    disabled
                  />
                </div>
                
                <div className="form-group phone-field">
                  <label>Phone Number</label>
                  <PhoneInput
                    className="profile-phone-input"
                    international
                    defaultCountry={DEFAULT_PHONE_COUNTRY}
                    countryCallingCodeEditable={false}
                    // Trims typing at the selected country's real maximum, so a
                    // US number stops at 10 national digits instead of running
                    // on to the 15-digit global E.164 ceiling
                    limitMaxLength
                    placeholder="Enter phone number"
                    value={normalizePhoneInput(formData.phoneValue)}
                    onChange={(value) => {
                      const nextValue = normalizePhoneInput(value);
                      const parsedPhone = parsePhoneNumberFromString(nextValue);
                      setFormData({
                        ...formData,
                        phoneValue: nextValue,
                        countryCode: parsedPhone ? `+${parsedPhone.countryCallingCode}` : formData.countryCode,
                        phone: parsedPhone?.nationalNumber || '',
                        country: parsedPhone?.country ? getCountryNameFromIso(parsedPhone.country) : formData.country,
                      });
                    }}
                    disabled={!editMode}
                  />
                </div>

                <div className="form-group">
                  <label>Country</label>
                  <input
                    type="text"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    disabled={!editMode}
                    placeholder="Country auto-fills from code, but you can edit it"
                  />
                </div>

                <div className="form-group">
                  <label>State</label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    disabled={!editMode}
                    placeholder="Enter state"
                  />
                </div>

                {/* Documents Section */}
                <div className="documents-section">
                  <div className="documents-header">
                    <h3>Identification Documents</h3>
                    {editMode && documents.length < 3 && (
                      <button className="add-document-btn" onClick={addDocument}>
                        <span className="plus-icon">+</span> Add Document
                      </button>
                    )}
                  </div>
                  
                  {documents.map((doc, index) => (
                    <div key={doc.id} className="document-card">
                      <div className="document-card-header">
                        <span className="document-number">Document {index + 1}</span>
                        {editMode && documents.length > 1 && (
                          <button 
                            className="remove-document-small-btn"
                            onClick={() => removeDocument(doc.id)}
                          >
                            ✕
                          </button>
                        )}
                      </div>
                      
                      <div className="document-fields">
                        <div className="doc-field">
                          <label>Document Type</label>
                          <select
                            value={doc.type}
                            onChange={(e) => updateDocumentField(doc.id, 'type', e.target.value)}
                            disabled={!editMode}
                          >
                            <option value="">Select type</option>
                            {ID_PROOF_OPTIONS.filter(
                              (option) =>
                                option.value === doc.type ||
                                !documents.some((d) => d.id !== doc.id && d.type === option.value)
                            ).map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="doc-field">
                          <label>Document Number</label>
                          <input
                            type="text"
                            value={doc.number}
                            placeholder="Enter document number"
                            onChange={(e) => updateDocumentField(doc.id, 'number', e.target.value)}
                            disabled={!editMode}
                          />
                        </div>

                        <div className="doc-field">
                          <label>Upload File</label>
                          <div className="doc-upload-wrapper">
                            <button
                              type="button"
                              className="doc-upload-btn"
                              onClick={() => proofInputRefs.current[doc.id]?.click()}
                              disabled={!editMode || uploadingDocId === doc.id}
                            >
                              {uploadingDocId === doc.id ? 'Uploading...' : (doc.fileName ? 'Change File' : 'Choose File')}
                            </button>
                            <input
                              ref={(el) => { proofInputRefs.current[doc.id] = el; }}
                              type="file"
                              accept=".pdf,image/*"
                              style={{ display: 'none' }}
                              onChange={(e) => handleDocumentFileSelect(doc.id, e)}
                            />
                          </div>
                        </div>
                      </div>

                      {doc.fileName && (
                        <div className="uploaded-file-info">
                          <span className="file-name-icon">📄</span>
                          <span className="file-name-text">{doc.fileName}</span>
                          {doc.fileUrl && (
                            <a href={doc.fileUrl} target="_blank" rel="noreferrer" className="file-view-link">
                              View
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* <div className="form-group">
                  <label>Date of Birth</label>
                  <input
                    type="date"
                    value={formData.dateOfBirth}
                    onChange={(e) => setFormData({...formData, dateOfBirth: e.target.value})}
                    disabled={!editMode}
                  />
                </div> */}
              </div>
              
              {/* Save button moved to header actions */}
            </div>
          </div>
        );

      case 'addresses':
        return (
          <div className="account-card">
            <div className="section-header">
              <div className="header-left">
                <h2>My Addresses</h2>
                {addressesTotal > 0 && (
                  <span className="address-count">
                    Total {addressesTotal} address{addressesTotal !== 1 ? 'es' : ''}
                  </span>
                )}
              </div>
              <div className="header-right">
                <div className="limit-select">
                  <label htmlFor="addresses-limit">Show</label>
                  <select
                    id="addresses-limit"
                    className="limit-dropdown"
                    value={String(addressesLimit)}
                    onChange={handleAddressesLimitChange}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value="All">All</option>
                  </select>
                </div>
                <button className="add-btn" onClick={handleAddAddress}>+ Add Address</button>
              </div>
            </div>
            
            <div className="addresses-grid">
              {loadingAddresses ? (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                  <p>Loading addresses...</p>
                </div>
              ) : addresses.length === 0 ? (
                <div className="no-data-container">
                  <p>No addresses found. Click "+ Add Address" to create one.</p>
                </div>
              ) : (
                addresses.map(address => (
                  <div key={address.uuid || address.id} className="address-card">
                    <div className="address-header">
                      <h3>Shipping Address</h3>
                      {address.isDefault && <span className="default-badge">Default</span>}
                    </div>
                    
                    <div className="address-info">
                      <p><strong>Full Name:</strong> {address.name}</p>
                      <p><strong>Email Address:</strong> {address.email}</p>
                      <p><strong>Phone:</strong> {address.mobile || address.phone}</p>
                      <p><strong>Door/Apt Number:</strong> {address.apartment_number}</p>
                      <p><strong>Street Address:</strong> {address.address || address.street}</p>
                      <p><strong>City:</strong> {address.city}</p>
                      <p><strong>State:</strong> {address.state}</p>
                      <p><strong>Zipcode:</strong> {address.pincode || address.zip}</p>
                    </div>
                    
                    <div className="address-actions">
                      <button className="edit-btn" onClick={() => handleEditAddress(address.uuid || address.id)}>Edit</button>
                      <button className="delete-btn" onClick={() => handleDeleteAddress(address.uuid || address.id)}>Delete</button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Pagination Footer */}
            {addresses.length > 0 && (
              <div className="addresses-footer">
                <div className="footer-left">
                  {(() => {
                    const totalCount = addressesTotal ?? addresses.length;
                    const start = totalCount === 0 ? 0 : (addressesPageDisplay - 1) * (String(addressesLimit).toLowerCase() === 'all' ? 100000 : Number(addressesLimit)) + 1;
                    const end = Math.min(addressesPageDisplay * (String(addressesLimit).toLowerCase() === 'all' ? 100000 : Number(addressesLimit)), totalCount);
                    return (
                      <span className="footer-summary">
                        Showing {String(addressesLimit).toLowerCase() === 'all' ? 'All' : `${start}-${end}`} of {totalCount} address{totalCount !== 1 ? 'es' : ''}
                      </span>
                    );
                  })()}
                </div>
                <div className="footer-right pagination-controls">
                  <button
                    type="button"
                    className="page-btn"
                    onClick={handleAddressesPrevPage}
                    disabled={addressesPageDisplay <= 1 || loadingAddresses}
                  >
                    Previous
                  </button>
                  <span className="page-indicator">
                    Page {addressesPageDisplay}{addressesTotal ? ` of ${Math.ceil(addressesTotal / (String(addressesLimit).toLowerCase() === 'all' ? 100000 : Number(addressesLimit)))}` : ''}
                  </span>
                  <button
                    type="button"
                    className="page-btn"
                    onClick={handleAddressesNextPage}
                    disabled={!addressesHasMore || loadingAddresses}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        );

      case 'orders':
        return (
          <div className="account-section">
            {orderId && orderDetailLoading && (
              <div className="account-inline-state">Loading order…</div>
            )}
            {orderId && !orderDetailLoading && orderDetailError && (
              <div className="account-inline-state account-inline-state--error">
                <p>{orderDetailError}</p>
                <button type="button" className="btn-primary" onClick={handleCloseOrderDetails}>
                  Back to My Orders
                </button>
              </div>
            )}
            {selectedOrder && !orderDetailLoading && (
              <OrderDetailsPage order={selectedOrder} onBack={handleCloseOrderDetails} />
            )}
            {!orderId && <MyOrders onOrderClick={handleOrderClick} />}
          </div>
        );

      case 'subscriptions':
        return (
          <div className="account-card">
            <div className="section-header">
              <div className="header-left">
                <h2>Subscriptions</h2>
                {subsTotal > 0 && (
                  <span className="subscription-count">
                    Total {subsTotal} subscription{subsTotal !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
              <div className="header-right">
                <div className="limit-select">
                  <label htmlFor="subscriptions-limit">Show</label>
                  <select
                    id="subscriptions-limit"
                    className="limit-dropdown"
                    value={String(subsLimit)}
                    onChange={handleSubsLimitChange}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value="All">All</option>
                  </select>
                </div>
              </div>
            </div>
            
            {subsLoading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading subscriptions...</p>
              </div>
            ) : subscriptions.length === 0 ? (
              <div className="no-data-container">
                <p>No subscriptions found for your account.</p>
              </div>
            ) : (
              <>
                <div className="subscriptions-list">
                  {subscriptions.map((sub) => {
                    const productName = sub.product?.name || 'Product';
                    const price = sub.total_amount ?? 0;
                    const freqDays = Number(sub.billing_duration || 0);
                    const frequency =
                      freqDays === 0 || freqDays < 7
                        ? 'One time purchase'
                        : freqDays >= 30
                        ? `${Math.floor(freqDays / 30)} month${freqDays >= 60 ? 's' : ''}`
                        : `${Math.floor(freqDays / 7)} week${freqDays >= 14 ? 's' : ''}`;
                    const status = (sub.status || '').toLowerCase();
                    // Helper function to get product image URL
                    const getProductImageUrl = (imageArray) => {
                      if (!imageArray || imageArray.length === 0) {
                        return '/product-placeholder.png';
                      }
                      const imageUrl = imageArray[0];
                      if (imageUrl.startsWith('http')) {
                        return imageUrl;
                      }
                      const base = (process.env.REACT_APP_API_BASE_URL).replace('/api/v1', '');
                      const clean = String(imageUrl).replace(/^\/+/, '').replace(/^uploads\/+/i, '');
                      return `${base}/uploads/${clean}`;
                    };

                    return (
                      <div key={sub.uuid || sub._id} className="subscription-card">
                        <div className="subscription-header">
                          <div className="subscription-product-info">
                            <img 
                              src={getProductImageUrl(sub.product?.image || sub.product?.product_image || sub.product?.images)} 
                              alt={productName}
                              className="subscription-product-image"
                            />
                            <div>
                              <h3>{productName}</h3>
                              <p className="transaction-id">Transaction ID: {sub.transaction_uuid || 'N/A'}</p>
                              <p className="order-id">Order ID: {sub.order_uuid || 'N/A'}</p>
                            </div>
                          </div>
                          <span className={`status-badge ${status === 'active' ? 'active' : 'paused'}`}>
                            {status.toUpperCase()}
                          </span>
                        </div>
                        
                        <div className="subscription-details">
                          {/* <div className="detail-row">
                            <span>Next Delivery:</span>
                            <span>{nextDate}</span>
                          </div> */}
                          <div className="detail-row">
                            <span>Frequency:</span>
                            <span>{frequency}</span>
                          </div>
                          <div className="detail-row">
                            <span>Price:</span>
                            <span>${Number(price).toFixed(2)}</span>
                          </div>
                        </div>
                        
                        {/* <div className="subscription-actions">
                          <button className="change-btn" disabled onClick={() => handleChangeSubscriptionFrequency(sub.uuid)}>Change Frequency</button>
                          <button className="pause-btn" disabled onClick={() => handlePauseSubscription(sub.uuid)}>Pause</button>
                          <button className="cancel-btn" disabled onClick={() => handleCancelSubscription(sub.uuid)}>Cancel</button>
                        </div> */}
                      </div>
                    );
                  })}
                </div>

                {/* Pagination Footer */}
                <div className="subscriptions-footer">
                  <div className="footer-left">
                    {(() => {
                      const totalCount = subsTotal ?? subscriptions.length;
                      const start = totalCount === 0 ? 0 : (subsPageDisplay - 1) * (String(subsLimit).toLowerCase() === 'all' ? 100000 : Number(subsLimit)) + 1;
                      const end = Math.min(subsPageDisplay * (String(subsLimit).toLowerCase() === 'all' ? 100000 : Number(subsLimit)), totalCount);
                      return (
                        <span className="footer-summary">
                          Showing {String(subsLimit).toLowerCase() === 'all' ? 'All' : `${start}-${end}`} of {totalCount} subscription{totalCount !== 1 ? 's' : ''}
                        </span>
                      );
                    })()}
                  </div>
                  <div className="footer-right pagination-controls">
                    <button
                      type="button"
                      className="page-btn"
                      onClick={handleSubsPrevPage}
                      disabled={subsPageDisplay <= 1 || subsLoading}
                    >
                      Previous
                    </button>
                    <span className="page-indicator">
                      Page {subsPageDisplay}{subsTotal ? ` of ${Math.ceil(subsTotal / (String(subsLimit).toLowerCase() === 'all' ? 100000 : Number(subsLimit)))}` : ''}
                    </span>
                    <button
                      type="button"
                      className="page-btn"
                      onClick={handleSubsNextPage}
                      disabled={!subsHasMore || subsLoading}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        );

      case 'settings':
        return (
          <div className="account-card">
            <h2>Settings</h2>
            
            <div className="settings-sections">
              <div className="settings-section">
                <h3>Security</h3>
                <div className="password-form">
                  <div className="form-group password-field-group">
                    <label>Current Password</label>
                    <div className="password-input-wrapper">
                      <input
                        type={showCurrentPassword ? "text" : "password"}
                        value={passwordData.currentPassword}
                        onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                      />
                      <button 
                        type="button" 
                        className="password-toggle-btn"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      >
                        {showCurrentPassword ? (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                        ) : (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                        )}
                      </button>
                    </div>
                  </div>
                  
                  <div className="form-group password-field-group">
                    <label>New Password</label>
                    <div className="password-input-wrapper">
                      <input
                        type={showNewPassword ? "text" : "password"}
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                      />
                      <button 
                        type="button" 
                        className="password-toggle-btn"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                      >
                        {showNewPassword ? (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                        ) : (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                        )}
                      </button>
                    </div>
                  </div>
                  
                  <div className="form-group password-field-group">
                    <label>Confirm Password</label>
                    <div className="password-input-wrapper">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        value={passwordData.confirmPassword}
                        onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                      />
                      <button 
                        type="button" 
                        className="password-toggle-btn"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                        ) : (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                        )}
                      </button>
                    </div>
                  </div>
                  
                  <button className="update-btn" onClick={handlePasswordUpdate}>
                    Update Password
                  </button>
                </div>
              </div>
              
              {/* <div className="settings-section">
                <h3>Notifications</h3>
                <div className="notification-toggles">
                  <div className="toggle-item">
                    <span>Email Updates</span>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={notificationSettings.emailUpdates}
                        onChange={() => handleNotificationToggle('emailUpdates')}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                  
                  <div className="toggle-item">
                    <span>SMS Alerts</span>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={notificationSettings.smsAlerts}
                        onChange={() => handleNotificationToggle('smsAlerts')}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                  
                  <div className="toggle-item">
                    <span>Promotions</span>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={notificationSettings.promotions}
                        onChange={() => handleNotificationToggle('promotions')}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                  
                  <div className="toggle-item">
                    <span>Subscription Reminders</span>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={notificationSettings.subscriptionReminders}
                        onChange={() => handleNotificationToggle('subscriptionReminders')}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                </div>
              </div> */}
              
              <div className="settings-section">
                <h3>Privacy</h3>
                <div className="privacy-info">
                  <p>Your privacy and data security are important to us. We protect your personal information and only use it to provide you with the best service experience.</p>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="account-page">
      <div className="account-container">
        {/* Sidebar */}
        <aside className="account-sidebar">
          <div className="sidebar-header">
            <div className="sidebar-identity">
              <div className="sidebar-avatar">
                {profileImageUrl ? (
                  <img src={profileImageUrl} alt="" />
                ) : (
                  <span aria-hidden="true">
                    {`${formData.firstName?.[0] || ''}${formData.lastName?.[0] || ''}`.toUpperCase() || '👤'}
                  </span>
                )}
              </div>
              <div className="sidebar-identity-text">
                <h2>
                  {[formData.firstName, formData.lastName].filter(Boolean).join(' ') || 'My Account'}
                </h2>
                {formData.email && <p title={formData.email}>{formData.email}</p>}
              </div>
            </div>
          </div>
          
          <nav className="sidebar-nav" aria-label="Account sections">
            {sidebarItems.map(item => (
              <Link
                key={item.id}
                to={item.path}
                className={`sidebar-item ${activeSection === item.id ? 'active' : ''}`}
                aria-current={activeSection === item.id ? 'page' : undefined}
              >
                <span className="item-icon">{item.icon}</span>
                <span className="item-label">{item.label}</span>
              </Link>
            ))}
          </nav>
          
          <div className="sidebar-footer">
            <button className="sign-out-btn" onClick={handleSignOut}>
              <span className="item-icon">🚪</span>
              <span className="item-label">Sign Out</span>
            </button>
          </div>
        </aside>
        
        {/* Main Content */}
        <main className="account-content">
          {renderContent()}
        </main>
      </div>
      <AddressFormModal 
        show={showAddressForm} 
        editing={editingAddress !== null}
        formData={addressFormData}
        onCancel={handleAddressFormCancel}
        onSubmit={handleAddressFormSubmit}
        onInputChange={handleAddressInputChange}
      />
    </div>
  );
}
